import { useEffect, useState, useRef, useMemo } from "react";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Plus, Upload, Trash2, Pencil, Search, Briefcase, ArrowUp, ArrowDown, Eye, Save, Loader2, StickyNote } from "lucide-react";
import { toast } from "sonner";
import ContactFormDialog from "../components/contacts/ContactFormDialog";

const PAGE_SIZE = 30;
const SORT_KEYS = ["first_name", "last_name", "email", "company_name", "position", "city", "owner_id"];

export default function Leads() {
    const { user, hasPermission } = useAuth();
    const [items, setItems] = useState([]);
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [sort, setSort] = useState({ key: null, dir: 0 });
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [selected, setSelected] = useState(new Set());
    // Contact detail viewer (read-only fields + editable notes)
    const [detail, setDetail] = useState(null);
    const [notesDraft, setNotesDraft] = useState("");
    const [savingNotes, setSavingNotes] = useState(false);
    const fileRef = useRef();

    const canManage = hasPermission("contacts_manage");
    const canBulkDelete = canManage;
    const canAssignOwner = hasPermission("contacts_global_view");
    const canCreateCompany = hasPermission("companies_manage");
    const canManageDeals = hasPermission("deals_manage");

    const load = async () => {
        const { data } = await api.get("/leads", { params: q ? { q } : {} });
        setItems(data);
        setSelected(new Set());
    };
    useEffect(() => {
        load();
        api.get("/admin/users/lite").then(({data})=>setUsers(data)).catch(()=>{});
        /* eslint-disable-next-line */
    }, []);
    useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

    const ownerName = (id) => {
        const u = users.find(u => u.user_id === id);
        return u?.name || (id === user?.user_id ? user?.name : "—");
    };

    const cycleSort = (key) => {
        setSort((s) => {
            if (s.key !== key) return { key, dir: 1 };
            if (s.dir === 1) return { key, dir: 2 };
            return { key: null, dir: 0 };
        });
        setPage(1);
    };

    const sortIcon = (key) => {
        if (sort.key !== key) return null;
        return sort.dir === 1 ? <ArrowUp className="w-3 h-3 inline ml-1"/> : <ArrowDown className="w-3 h-3 inline ml-1"/>;
    };

    const sorted = useMemo(() => {
        if (!sort.key || sort.dir === 0) return items;
        const valOf = (it) => {
            if (sort.key === "owner_id") return ownerName(it.owner_id);
            return it[sort.key] || "";
        };
        return [...items].sort((a, b) => {
            const av = valOf(a).toString().toLowerCase();
            const bv = valOf(b).toString().toLowerCase();
            return sort.dir === 1 ? av.localeCompare(bv) : bv.localeCompare(av);
        });
        /* eslint-disable-next-line */
    }, [items, sort, users]);

    const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const pageItems = sorted.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);

    const allOnPageSelected = pageItems.length > 0 && pageItems.every(l => selected.has(l.lead_id));

    const togglePageAll = () => {
        const next = new Set(selected);
        if (allOnPageSelected) pageItems.forEach(l => next.delete(l.lead_id));
        else pageItems.forEach(l => next.add(l.lead_id));
        setSelected(next);
    };

    const toggleOne = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };

    const openNew = () => { setEditing(null); setOpen(true); };
    const openEdit = (l) => { setEditing(l); setOpen(true); };
    const openDetail = (l) => { setDetail(l); setNotesDraft(l.notes || ""); };
    const closeDetail = () => { setDetail(null); setNotesDraft(""); };

    const saveNotes = async () => {
        if (!detail) return;
        setSavingNotes(true);
        try {
            const { data } = await api.patch(`/leads/${detail.lead_id}`, { notes: notesDraft });
            // Optimistic local update so the list reflects the change immediately.
            setItems((arr) => arr.map((x) => (x.lead_id === detail.lead_id ? { ...x, notes: notesDraft } : x)));
            setDetail(data || { ...detail, notes: notesDraft });
            toast.success("Nota guardada");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar la nota");
        } finally {
            setSavingNotes(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("¿Eliminar contacto?")) return;
        await api.delete(`/leads/${id}`);
        toast.success("Eliminado"); load();
    };

    const removeSelected = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        if (!window.confirm(`¿Eliminar ${ids.length} contacto(s) seleccionado(s)? Esta acción es irreversible.`)) return;
        try {
            const { data } = await api.post("/leads/bulk-delete", { lead_ids: ids });
            toast.success(`Eliminados: ${data.deleted}`);
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const convert = async (lead) => {
        try {
            const { data: funnels } = await api.get("/funnels");
            const f = funnels[0];
            if (!f) { toast.error("Crea un embudo primero"); return; }
            const stage = f.stages.slice().sort((a,b)=>a.order-b.order)[0];
            await api.post(`/leads/${lead.lead_id}/convert`, { funnel_id: f.funnel_id, stage_id: stage.stage_id });
            toast.success("Convertido a Deal");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/import/leads", fd);
            toast.success(`Importados: ${data.inserted} (omitidos: ${data.skipped})`);
            load();
        } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
        finally { e.target.value = ""; }
    };

    const Th = ({ k, label, className = "" }) => (
        SORT_KEYS.includes(k) ? (
            <th
                className={`text-left px-4 py-3 cursor-pointer select-none hover:text-orange-600 transition-colors ${className}`}
                onClick={() => cycleSort(k)}
                data-testid={`leads-sort-${k}`}
            >
                {label}{sortIcon(k)}
            </th>
        ) : (
            <th className={`text-left px-4 py-3 ${className}`}>{label}</th>
        )
    );

    return (
        <Layout
            title="Contactos"
            subtitle="Tus leads y contactos comerciales"
            actions={
                <>
                    <input type="file" ref={fileRef} accept=".xlsx,.csv" onChange={onUpload} className="hidden" data-testid="leads-import-input"/>
                    {canBulkDelete && selected.size > 0 && (
                        <Button variant="outline" onClick={removeSelected} className="text-red-600 hover:text-red-700 border-red-300" data-testid="leads-bulk-delete-button">
                            <Trash2 className="w-4 h-4 mr-2"/>Eliminar seleccionados ({selected.size})
                        </Button>
                    )}
                    {canManage && <Button variant="outline" onClick={()=>fileRef.current?.click()} data-testid="leads-import-button"><Upload className="w-4 h-4 mr-2"/>Importar</Button>}
                    {canManage && <Button onClick={openNew} data-testid="leads-new-button"><Plus className="w-4 h-4 mr-2"/>Nuevo Contacto</Button>}
                </>
            }
        >
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="p-3 border-b border-slate-200 flex items-center gap-2">
                    <Search className="w-4 h-4 text-slate-400"/>
                    <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Buscar por nombre, email, empresa..." className="border-0 focus-visible:ring-0 shadow-none" data-testid="leads-search-input"/>
                </div>
                <div className="overflow-x-auto scrollbar-thin">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                                {canBulkDelete && (
                                    <th className="px-3 py-3 w-10">
                                        <Checkbox
                                            checked={allOnPageSelected}
                                            onCheckedChange={togglePageAll}
                                            data-testid="leads-select-all"
                                            aria-label="Seleccionar todos"
                                        />
                                    </th>
                                )}
                                <Th k="first_name" label="Nombre"/>
                                <Th k="last_name" label="Apellido"/>
                                <Th k="email" label="Correo"/>
                                <Th k="phone" label="Teléfono"/>
                                <Th k="position" label="Cargo"/>
                                <Th k="city" label="Ciudad"/>
                                <Th k="company_name" label="Empresa"/>
                                <Th k="owner_id" label="Propietario"/>
                                <th className="text-right px-4 py-3 whitespace-nowrap">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.map((l) => (
                                <tr key={l.lead_id} className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${selected.has(l.lead_id) ? "bg-orange-50/40" : ""}`} data-testid={`lead-row-${l.lead_id}`} onClick={()=>openDetail(l)}>
                                    {canBulkDelete && (
                                        <td className="px-3 py-3" onClick={(e)=>e.stopPropagation()}>
                                            <Checkbox
                                                checked={selected.has(l.lead_id)}
                                                onCheckedChange={()=>toggleOne(l.lead_id)}
                                                data-testid={`lead-select-${l.lead_id}`}
                                                aria-label="Seleccionar contacto"
                                            />
                                        </td>
                                    )}
                                    <td className="px-4 py-3 font-medium text-slate-900">{l.first_name || "—"}</td>
                                    <td className="px-4 py-3 text-slate-700">{l.last_name || "—"}</td>
                                    <td className="px-4 py-3 text-slate-600">{l.email||"—"}</td>
                                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">{l.phone||"—"}</td>
                                    <td className="px-4 py-3 text-slate-600">{l.position||"—"}</td>
                                    <td className="px-4 py-3 text-slate-600">{l.city||"—"}</td>
                                    <td className="px-4 py-3 text-slate-600">{l.company_name||"—"}</td>
                                    <td className="px-4 py-3 text-slate-600 text-xs">{ownerName(l.owner_id)}</td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e)=>e.stopPropagation()}>
                                        <button onClick={()=>openDetail(l)} title="Ver detalle" data-testid={`lead-view-${l.lead_id}`} className="inline-flex p-1.5 rounded hover:bg-slate-100 mx-0.5">
                                            <Eye className="w-3.5 h-3.5 text-slate-600"/>
                                        </button>
                                        {canManageDeals && <button onClick={()=>convert(l)} title="Convertir a Deal" data-testid={`lead-convert-${l.lead_id}`} className="inline-flex p-1.5 rounded hover:bg-orange-50 mx-0.5"><Briefcase className="w-3.5 h-3.5 text-orange-600"/></button>}
                                        {canManage && <button onClick={()=>openEdit(l)} title="Editar" data-testid={`lead-edit-${l.lead_id}`} className="inline-flex p-1.5 rounded hover:bg-slate-100 mx-0.5"><Pencil className="w-3.5 h-3.5 text-slate-600"/></button>}
                                        {canManage && <button onClick={()=>remove(l.lead_id)} title="Eliminar" data-testid={`lead-delete-${l.lead_id}`} className="inline-flex p-1.5 rounded hover:bg-red-50 mx-0.5"><Trash2 className="w-3.5 h-3.5 text-red-500"/></button>}
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && <tr><td colSpan={canBulkDelete ? 10 : 9} className="px-4 py-12 text-center text-slate-400">Sin contactos</td></tr>}
                        </tbody>
                    </table>
                </div>
                {sorted.length > 0 && (
                    <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between text-sm" data-testid="leads-pagination">
                        <div className="text-slate-500">
                            Mostrando {(page-1)*PAGE_SIZE + 1}–{Math.min(page*PAGE_SIZE, sorted.length)} de {sorted.length}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} data-testid="leads-page-prev">Anterior</Button>
                            <span className="text-slate-600 font-mono text-xs px-2">Página {page} / {totalPages}</span>
                            <Button variant="outline" size="sm" onClick={()=>setPage(p=>Math.min(totalPages, p+1))} disabled={page>=totalPages} data-testid="leads-page-next">Siguiente</Button>
                        </div>
                    </div>
                )}
            </div>

            <ContactFormDialog open={open} onOpenChange={setOpen} contact={editing} onSaved={load} users={users} canAssignOwner={canAssignOwner} canCreateCompany={canCreateCompany} currentUserId={user?.user_id || ""} />

            {/* Contact detail viewer with editable notes */}
            <Dialog open={!!detail} onOpenChange={(o)=>{ if (!o) closeDetail(); }}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-detail-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Eye className="w-4 h-4 text-orange-600" />
                            {detail ? `${detail.first_name || ""} ${detail.last_name || ""}`.trim() || "Contacto" : "Contacto"}
                        </DialogTitle>
                    </DialogHeader>
                    {detail && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Correo</div>
                                    <div className="text-slate-900 break-all" data-testid="lead-detail-email">{detail.email || "—"}</div>
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Teléfono</div>
                                    <div className="text-slate-900 font-mono">{detail.phone || "—"}</div>
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Cargo</div>
                                    <div className="text-slate-900">{detail.position || "—"}</div>
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Empresa</div>
                                    <div className="text-slate-900">{detail.company_name || "—"}</div>
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Ciudad</div>
                                    <div className="text-slate-900">{detail.city || "—"}</div>
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Origen</div>
                                    <div className="text-slate-900">{detail.source || "—"}</div>
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Estado</div>
                                    <div className="text-slate-900">{detail.status || "—"}</div>
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-wider text-slate-500">Propietario</div>
                                    <div className="text-slate-900">{ownerName(detail.owner_id)}</div>
                                </div>
                                {detail.created_at && (
                                    <div className="sm:col-span-2">
                                        <div className="text-[11px] uppercase tracking-wider text-slate-500">Creado</div>
                                        <div className="text-slate-900 text-xs">{new Date(detail.created_at).toLocaleString("es")}</div>
                                    </div>
                                )}
                            </div>

                            <div className="border-t border-slate-200 pt-4">
                                <Label className="flex items-center gap-2 mb-1.5">
                                    <StickyNote className="w-4 h-4 text-orange-600" />Notas
                                </Label>
                                <Textarea
                                    rows={6}
                                    value={notesDraft}
                                    onChange={(e) => setNotesDraft(e.target.value)}
                                    placeholder="Añade aquí información relevante: contexto, intereses, próximos pasos..."
                                    data-testid="lead-detail-notes"
                                />
                                <p className="text-xs text-slate-500 mt-1.5">
                                    {notesDraft.length} caracteres · {detail.notes === notesDraft ? "Sin cambios" : "Cambios sin guardar"}
                                </p>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="ghost" onClick={closeDetail} data-testid="lead-detail-close">Cerrar</Button>
                        {canManage && <Button variant="outline" onClick={() => { if (detail) { openEdit(detail); closeDetail(); } }} data-testid="lead-detail-edit-all"><Pencil className="w-4 h-4 mr-2" />Editar todo</Button>}
                        {canManage && <Button
                            onClick={saveNotes}
                            disabled={savingNotes || !detail || (detail.notes || "") === notesDraft}
                            data-testid="lead-detail-save-notes"
                        >
                            {savingNotes ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
                            ) : (
                                <><Save className="w-4 h-4 mr-2" />Guardar nota</>
                            )}
                        </Button>}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
